import { isValidPhoneNumber } from 'react-phone-number-input';
import { countryCodeOf, isValidPostalCode } from '../../pageInside/addressLocation/addressCountryRules';

// Field rules of the /user_Shoosing/:token account form. They mirror the
// server exactly -- userSignupValidator (names, email, password), the User
// model (no "password" in the password) and eventSignupProvisioning's
// profile checks (phone, address) -- so the browser never accepts what the
// server refuses. Each error is a stable code from auth/authErrors.js, the
// same code the server answers with.
const NAME_PATTERN = /^[A-Za-z_ -]+$/;
const EMAIL_PATTERN = /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,8})+$/;
const PASSWORD_PATTERN = /^(?=.*\d)(?=.*[a-z ])(?=.*[A-Z ]).{1,16}$/;
const STREET_PATTERN = /^[\p{L}\p{M}\p{N} .,'’()/&#°ºª:;-]+$/u;
const PLACE_PATTERN = /^[\p{L}\p{M}\p{N} .,'’()/&-]+$/u;

// DOM order of the form, used to focus the first invalid field.
export const ACCOUNT_FIELDS = ['firstname', 'lastname', 'email', 'password', 'passwordConfirmation', 'phone', 'Address1', 'Address2', 'country', 'postalCode', 'state', 'city'];

const text = value => String(value ?? '').trim();
const nameError = (value, prefix) => {
  if (!text(value)) return `${prefix}_REQUIRED`;
  if (!NAME_PATTERN.test(value)) return `${prefix}_INVALID`;
  if (value.length > 30) return `${prefix}_TOO_LONG`;
  return null;
};
const passwordError = value => {
  if (!value) return 'PASSWORD_REQUIRED';
  if (value.length < 7) return 'PASSWORD_TOO_SHORT';
  if (value.length > 16) return 'PASSWORD_TOO_LONG';
  if (!PASSWORD_PATTERN.test(value)) return 'PASSWORD_TOO_WEAK';
  if (value.toLowerCase().includes('password')) return 'PASSWORD_CONTAINS_PASSWORD';
  return null;
};
const streetError = (value, required) => {
  if (!text(value)) return required ? 'ADDRESS_REQUIRED' : null;
  return text(value).length > 200 || !STREET_PATTERN.test(text(value)) ? 'ADDRESS_INVALID' : null;
};
const placeError = (value, prefix) => {
  if (!text(value)) return `${prefix}_REQUIRED`;
  return text(value).length > 120 || !PLACE_PATTERN.test(text(value)) ? `${prefix}_INVALID` : null;
};

// { field: CODE } for every invalid field; {} when the form can be sent.
// The role profile needs all four location fields (server hasRoleProfile).
export const validateEventAccount = form => {
  const location = form.location || {};
  const email = text(form.email);
  const errors = {
    firstname: nameError(form.firstname, 'FIRSTNAME'),
    lastname: nameError(form.lastname, 'LASTNAME'),
    email: !email ? 'EMAIL_REQUIRED' : !EMAIL_PATTERN.test(email) ? 'INVALID_EMAIL' : email.length < 10 || email.length > 70 ? 'EMAIL_LENGTH_INVALID' : null,
    password: passwordError(form.password),
    passwordConfirmation: !form.passwordConfirmation ? 'PASSWORD_CONFIRMATION_REQUIRED' : form.passwordConfirmation !== form.password ? 'PASSWORD_CONFIRMATION_MISMATCH' : null,
    phone: !form.phone ? 'PHONE_REQUIRED' : !isValidPhoneNumber(form.phone) ? 'INVALID_PHONE' : null,
    Address1: streetError(form.Address1, true),
    Address2: streetError(form.Address2, false),
    country: countryCodeOf(location.country) ? null : 'COUNTRY_REQUIRED',
    postalCode: !text(location.postalCode) ? 'POSTAL_CODE_REQUIRED' : !isValidPostalCode(location.country, location.postalCode) ? 'INVALID_POSTAL_CODE' : null,
    state: placeError(location.state, 'STATE'),
    city: placeError(location.city, 'CITY'),
  };
  return Object.fromEntries(Object.entries(errors).filter(([, code]) => code));
};

export const firstInvalidField = errors => ACCOUNT_FIELDS.find(field => errors[field]) || null;
